import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Wallet, QrCode, Shield, CheckCircle, Zap, Lock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { LiveMetricsStrip } from '../components/LiveMetricsStrip';
import { SEO, jsonLdSchemas } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 },
  },
};

export default function Home() {
  const { t, language } = useLanguage();
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to their role-based home
  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      const roleHomeMap = {
        sender: '/app/sender',
        merchant: '/app/merchant',
        admin: '/admin/dashboard',
        beneficiary: '/beneficiary'
      };
      const targetPath = roleHomeMap[user.role] || '/app/sender';
      navigate(targetPath, { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If authenticated, don't render (redirect is happening)
  if (isAuthenticated && user) {
    return null;
  }

  return (
    <Layout>
      <SEO
        title="Authorize Family Purchases at Dominican Colmados"
        description="PeCash enables U.S. customers to authorize household purchases for family members at participating Dominican merchants. Secure, instant, and controlled."
        canonical="https://pecashglobal.com"
        jsonLd={jsonLdSchemas.organization}
        lang={language}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="hero-gradient absolute inset-0 -z-10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-32">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeInUp}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
                {t('hero.title')}{' '}
                <span className="text-emerald-300">{t('hero.titleHighlight')}</span>{' '}
                {t('hero.titleEnd')}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-emerald-100 leading-relaxed max-w-xl">
                {t('hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/app/sender">
                  <Button
                    data-testid="hero-cta-pay"
                    className="h-12 px-8 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 font-semibold shadow-lg shadow-emerald-900/30 transition-transform active:scale-95"
                  >
                    {t('hero.ctaPay')}
                  </Button>
                </Link>
                <Link to="/colmados">
                  <Button
                    data-testid="hero-cta-merchant"
                    variant="outline"
                    className="h-12 px-8 rounded-full border-2 border-emerald-300/50 text-white hover:bg-white/10 font-semibold"
                  >
                    {t('hero.ctaMerchant')}
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp} className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1683650666910-ecc317a12061?w=2400&h=1800&fit=crop&q=100"
                  alt="Happy Latino family at home"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Live Impact Metrics Strip - Only shows if real data exists */}
      <LiveMetricsStrip />

      {/* How It Works Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-sm font-medium text-emerald-700 uppercase tracking-wider mb-3">
              {t('howItWorks.subtitle')}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('howItWorks.title')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12"
          >
            {/* Step 1 */}
            <motion.div variants={fadeInUp} className="group relative p-8 bg-white rounded-3xl border border-slate-100 hover:border-emerald-100 transition-all duration-500" data-testid="step-1">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-colors">
                <Send className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('howItWorks.step1Title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('howItWorks.step1Desc')}</p>
            </motion.div>

            {/* Step 2 */}
            <motion.div variants={fadeInUp} className="group relative p-8 bg-white rounded-3xl border border-slate-100 hover:border-emerald-100 transition-all duration-500" data-testid="step-2">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                <Wallet className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('howItWorks.step2Title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('howItWorks.step2Desc')}</p>
            </motion.div>

            {/* Step 3 */}
            <motion.div variants={fadeInUp} className="group relative p-8 bg-white rounded-3xl border border-slate-100 hover:border-emerald-100 transition-all duration-500" data-testid="step-3">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6 group-hover:bg-amber-500 transition-colors">
                <QrCode className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('howItWorks.step3Title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('howItWorks.step3Desc')}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-sm font-medium text-emerald-700 uppercase tracking-wider mb-3">
              {t('benefits.subtitle')}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('benefits.title')}
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* For Supporters */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="p-8 bg-gradient-to-br from-emerald-50 to-white rounded-3xl border border-emerald-100"
              data-testid="benefits-senders"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{t('benefits.senderTitle')}</h3>
              </div>
              <ul className="space-y-4">
                {t('benefits.senderItems').map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* For Merchants */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="p-8 bg-gradient-to-br from-blue-50 to-white rounded-3xl border border-blue-100"
              data-testid="benefits-merchants"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{t('benefits.merchantTitle')}</h3>
              </div>
              <ul className="space-y-4">
                {t('benefits.merchantItems').map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-sm font-medium text-emerald-400 uppercase tracking-wider mb-3">
              {t('trust.subtitle')}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white">
              {t('trust.title')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            <motion.div variants={fadeInUp} className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700" data-testid="trust-encryption">
              <Lock className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('trust.encryption')}</h3>
              <p className="text-sm text-slate-400">{t('trust.encryptionDesc')}</p>
            </motion.div>
            <motion.div variants={fadeInUp} className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700" data-testid="trust-secure">
              <Shield className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('trust.secure')}</h3>
              <p className="text-sm text-slate-400">{t('trust.secureDesc')}</p>
            </motion.div>
            <motion.div variants={fadeInUp} className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700" data-testid="trust-fraud">
              <Zap className="w-8 h-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('trust.fraud')}</h3>
              <p className="text-sm text-slate-400">{t('trust.fraudDesc')}</p>
            </motion.div>
            <motion.div variants={fadeInUp} className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700" data-testid="trust-privacy">
              <Users className="w-8 h-8 text-purple-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('trust.privacy')}</h3>
              <p className="text-sm text-slate-400">{t('trust.privacyDesc')}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-emerald-600 to-emerald-700" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('cta.title')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
              {t('cta.subtitle')}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app/sender">
                <Button
                  data-testid="cta-sender"
                  className="h-12 px-8 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-semibold shadow-lg"
                >
                  {t('cta.startSending')}
                </Button>
              </Link>
              <Link to="/colmados">
                <Button
                  data-testid="cta-merchant"
                  variant="outline"
                  className="h-12 px-8 rounded-full border-2 border-white/50 text-white hover:bg-white/10 font-semibold"
                >
                  {t('cta.registerMerchant')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
